import { Box, Typography } from "@mui/material";
import horizontalArrow from "../../../../../assets/horizontalArrow.svg";
import CoveragesLogo from "../../../../../assets/CoveragesLogo.svg";
import DiscountsLogo from "../../../../../assets/DiscountsLogo.svg";
import React, { useState } from "react";
import downArrow from "../../../../../assets/downArrow.svg";
import { IPremiumPerVehicle } from "../../../../../models/user/policyDetails/PolicyDetails";
import { CoveragesStyles } from "../CoveragesStyles";
import Discounts from "../Discounts/Discounts";
import ResponsiveCoveragesStyles from "./ResponsiveCoveragesStyles";
import { useAppSelector } from "../../../../../store/hooks";
import { RootState } from "../../../../../store/store";
import AdditionalDeductibles from "../AdditionalDeductibles/AdditionalDeductibles";
import CoveragesAndLimitsStyles from "../CoveragesAndLimits/CoveragesAndLimitsStyles";
import { currencyFormat } from "../../../../../utils/Utility";
import { useTranslation } from "react-i18next";

interface IProps {
  perVehicledata: IPremiumPerVehicle[];
  totalPremium: string | null;
  data: any[];
  additionalData: any[];
  lobCode: string | null;
}

const ResponsiveCoverages: React.FC<IProps> = ({
  perVehicledata,
  totalPremium,
  data,
  additionalData,
  lobCode,
}) => {
  const { coveragesAndDiscounts } = useAppSelector(
    (state: RootState) => state.policyDetails
  );

  const { t } = useTranslation();

  const [expandedItems, setExpandedItems] = useState<(boolean | undefined)[]>(
    []
  );

  const handleClick = (index: number) => {
    setExpandedItems((prevExpandedItems) => {
      const newExpandedItems = [...prevExpandedItems];
      newExpandedItems[index] = !newExpandedItems[index];
      for (let i = 0; i < newExpandedItems.length; i++) {
        if (i !== index) {
          newExpandedItems[i] = false;
        }
      }
      return newExpandedItems;
    });
  };

  const getPerVehicleSection = () => {
    let element: any = <Box>{t("No records found")}</Box>;
    if (perVehicledata && perVehicledata.length > 0) {
      element = perVehicledata.map((item, index) => {
        return (
          <Box
            key={`p-v-${index}`}
            sx={CoveragesStyles.vechilePremiumTextValues}
          >
            <Typography sx={CoveragesStyles.vechileValueText}>
              ${item.amount}
            </Typography>
            <Typography sx={CoveragesStyles.vechileNameText}>
              {`${item.manufactureYr}-${item.make}-${item.model}`}
            </Typography>
          </Box>
        );
      });
    }
    return element;
  };

  const getLimit = (dataString: string) => {
    let defaultVal = "-";
    const limitArray = dataString.split("/");
    if (limitArray[1]) {
      defaultVal = `${currencyFormat(
        "us",
        parseFloat(limitArray[0])
      )} / ${currencyFormat("us", parseFloat(limitArray[1]))}`;
    } else {
      defaultVal = /\d/.test(limitArray[0])
        ? `${currencyFormat("us", parseFloat(limitArray[0]))}`
        : dataString;
    }

    return defaultVal;
  };

  const getCoveragesSection = (data: any[]) => {
    let element: any = <Box>{t("No Coverage found")}</Box>;
    if (data && data.length > 0) {
      element = data.map((coverageItem, index) => {
        return (
          <Box
            key={`mob-coverage-list${index}`}
            sx={{
              ...CoveragesStyles.coveragesCard,
              backgroundColor: expandedItems[index] ? "#F5FAFC" : "#FFFFFF",
              width: expandedItems[index] ? "auto" : "auto",
              marginLeft: expandedItems[index] ? "-10px" : "0px",
              marginRight: expandedItems[index] ? "-10px" : "0px",
              boxShadow: expandedItems[index]
                ? "0px 2px 4px 0px #00000017 inset"
                : "none",
              padding: expandedItems[index]
                ? "22px 15px 20px 15px"
                : "15px 5px 20px 5px",
              borderRadius:
                index === expandedItems.length - 1
                  ? "0px 0px 10px 10px"
                  : "0px",
            }}
          >
            <Box sx={CoveragesStyles.coveragesHead} key={`c-mob-${index}`}>
              <Box>
                <Typography sx={CoveragesStyles.coveragesTextHeading}>
                  {coverageItem.coverageName}
                </Typography>
                <Typography sx={CoveragesStyles.perItemText}>
                  {coverageItem.coverageSubText
                    ? `(${coverageItem.coverageSubText})`
                    : ""}
                </Typography>
              </Box>
              <img
                onClick={() => handleClick(index)}
                src={expandedItems[index] ? downArrow : horizontalArrow}
                alt={expandedItems[index] ? "Down-arrow" : "Horizontal-arrow"}
              />
            </Box>
            <Box sx={CoveragesStyles.limits}>
              <Typography sx={CoveragesStyles.limitsText}>Limit</Typography>
              <Typography sx={CoveragesStyles.limitsCount}>
                {getLimit(coverageItem.limit)
                  ? getLimit(coverageItem.limit)
                  : "-"}
              </Typography>
            </Box>
            {expandedItems[index] && (
              <Box sx={CoveragesStyles.expandedItemsContainer}>
                <Box sx={CoveragesStyles.expandedItems}>
                  <Typography sx={CoveragesStyles.expanededItemkeyText}>
                    {t("Deductible")}
                  </Typography>
                  <Typography sx={CoveragesStyles.expanededItemvalueText}>
                    {coverageItem &&
                    coverageItem.deductible &&
                    !isNaN(coverageItem.deductible)
                      ? currencyFormat("us", coverageItem.deductible)
                      : coverageItem.deductible
                      ? coverageItem.deductible
                      : "-"}
                  </Typography>
                </Box>
              </Box>
            )}
            {lobCode !== "PA" && expandedItems[index] && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <Box sx={CoveragesStyles.expandedItems}>
                  <Typography sx={CoveragesStyles.expanededItemkeyText}>
                    {t("Premium")}
                  </Typography>
                  <Typography sx={CoveragesStyles.expanededItemvalueText}>
                    {coverageItem &&
                    coverageItem.coveragePremiumAmt &&
                    !isNaN(coverageItem.coveragePremiumAmt)
                      ? currencyFormat("us", coverageItem.coveragePremiumAmt)
                      : coverageItem.coveragePremiumAmt
                      ? coverageItem.coveragePremiumAmt
                      : "-"}
                  </Typography>
                </Box>
              </Box>
            )}
            {lobCode === "PA" &&
              expandedItems[index] &&
              Object.keys(data[0])
                .filter(
                  (fItem) =>
                    fItem !== "coverageName" &&
                    fItem !== "coverageSubText" &&
                    fItem !== "deductible" &&
                    fItem !== "limit" &&
                    fItem !== "description"
                )
                .map((cItem, cIndex) => {
                  return (
                    <Box
                      sx={CoveragesStyles.expandedItems}
                      key={`mob-dynamic-${cIndex}`}
                    >
                      <Typography sx={CoveragesStyles.expanededItemkeyText}>
                        {cItem}
                      </Typography>
                      <Typography sx={CoveragesStyles.expanededItemvalueText}>
                        {coverageItem[cItem] &&
                        coverageItem[cItem] &&
                        !isNaN(coverageItem[cItem])
                          ? currencyFormat("us", coverageItem[cItem])
                          : coverageItem[cItem]
                          ? coverageItem[cItem]
                          : "-"}
                      </Typography>
                    </Box>
                  );
                })}
          </Box>
        );
      });
    }
    return element;
  };

  return (
    <Box sx={CoveragesStyles.totalContainer}>
      <Box sx={CoveragesStyles.responsiveContainer}>
        <Box sx={CoveragesStyles.cardpremiums}>
          <Typography sx={CoveragesStyles.premiumText}>
            {t("Total Policy Premium")}
          </Typography>
          <Typography sx={CoveragesStyles.premiumValueText}>
            ${totalPremium}
          </Typography>
        </Box>
        {lobCode === "PA" ? (
          <Box sx={CoveragesStyles.cardTotalPremiums}>
            <Typography sx={CoveragesStyles.premiumText}>
              {t("Total Premium Per Vehicle")}
            </Typography>
            <Box sx={CoveragesStyles.vechilePremiumValues}>
              {getPerVehicleSection()}
            </Box>
          </Box>
        ) : (
          <></>
        )}
      </Box>
      <Box>
        <Box sx={ResponsiveCoveragesStyles.coverageAndLimitsHeading}>
          <Box>
            <img src={CoveragesLogo} alt="coverages logo" />
          </Box>
          <Box>
            <Typography
              sx={ResponsiveCoveragesStyles.coverageAndLimitsHeadingText}
            >
              {t("Coverages and limits")}
            </Typography>
          </Box>
        </Box>
        <Box sx={CoveragesStyles.coverages}>
          <Typography sx={CoveragesStyles.headText}>
            {t("Coverages")}
          </Typography>
          <Box>{getCoveragesSection(data)}</Box>
        </Box>
      </Box>
      {additionalData.length > 0 ? (
        <Box>
          <Box sx={ResponsiveCoveragesStyles.coverageAndLimitsHeading}>
            <Box>
              <img src={CoveragesLogo} alt="coverages logo" />
            </Box>
            <Box>
              <Typography
                sx={ResponsiveCoveragesStyles.coverageAndLimitsHeadingText}
              >
                {t("Additional Coverages and limits")}
              </Typography>
            </Box>
          </Box>
          <Box sx={CoveragesStyles.coverages}>
            <Typography sx={CoveragesStyles.headText}>
              {t("Coverages")}
            </Typography>
            <Box>{getCoveragesSection(additionalData)}</Box>
          </Box>
        </Box>
      ) : (
        <></>
      )}
      {coveragesAndDiscounts?.lobCode === "HO" ? (
        <Box sx={{ display: "grid", gap: "20px" }}>
          <Typography
            sx={CoveragesAndLimitsStyles.coverageAndLimitsHeadingText}
          >
            {t("Additional Deductibles")}
          </Typography>
          <AdditionalDeductibles />
        </Box>
      ) : (
        <></>
      )}
      {coveragesAndDiscounts?.discounts &&
      coveragesAndDiscounts.discounts.length > 0 ? (
        <Box sx={CoveragesStyles.mobileViewDiscounts}>
          <Discounts />
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};
export default ResponsiveCoverages;
